import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Typography, Box } from '@mui/material';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { Tooltip } from '@mui/material';
import heartLoading from '../../../assests/heartLoading.gif';

const PAGE_SIZE = 80;

export const AssetDetails = ({ assetData, searchQuery, height }) => {
  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [expandedCells, setExpandedCells] = useState(new Set());

  const toggleCell = (index, field) => {
    setExpandedCells((prev) => {
      const next = new Set(prev);
      const key = `${index}-${field}`;
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };
  const [columnSearch, setColumnSearch] = useState({
    name: "",
    type: "",
    is_cde: "",
    metadata: "",
    businessterm: "",
    transformationLogic: "",
    sample: "",
  });
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const tableContainerRef = useRef(null);

  const { assetsOverview, tableDetails } = assetData || {};

  const renderWithTooltip = (text, limit = 20) => {
    if (!text) return "N/A";
    const displayText = text.length > limit ? `${text.slice(0, limit)}...` : text;
    return (
      <Tooltip
        title={text}
        arrow
        placement="top"
        PopperProps={{ container: document.body, style: { zIndex: 10000 } }}
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: "#ece5e5ff",
              color: "black",
              fontSize: "10px",
              fontFamily: "Poppins",
              padding: "8px 10px",
              borderRadius: "6px",
              maxWidth: "400px",
              whiteSpace: "pre-wrap",
            },
          },
        }}
      >
        <span style={{ display: "inline-block", cursor: "pointer" }}>{displayText}</span>
      </Tooltip>
    );
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  const handleColumnSearchChange = (column, value) => {
    setColumnSearch((prev) => ({ ...prev, [column]: value }));
  };

  const filteredAndSortedData = useMemo(() => {
    if (!tableDetails || !Array.isArray(tableDetails)) return [];
    let filtered = [...tableDetails];

    Object.entries(columnSearch).forEach(([column, searchValue]) => {
      if (searchValue && searchValue.trim()) {
        filtered = filtered.filter((row) => {
          let cellValue = column === "is_cde" ? (row[column] ? "yes" : "no") : row[column] || "";
          return String(cellValue).toLowerCase().includes(searchValue.toLowerCase().trim());
        });
      }
    });

    if (sortColumn) {
      filtered.sort((a, b) => {
        let aVal = sortColumn === "is_cde" ? (a[sortColumn] ? "yes" : "no") : a[sortColumn];
        let bVal = sortColumn === "is_cde" ? (b[sortColumn] ? "yes" : "no") : b[sortColumn];
        if (aVal === null || aVal === undefined) aVal = "";
        if (bVal === null || bVal === undefined) bVal = "";
        const cmp = String(aVal).toLowerCase().localeCompare(String(bVal).toLowerCase());
        return sortOrder === "asc" ? cmp : -cmp;
      });
    }

    return filtered;
  }, [tableDetails, columnSearch, sortColumn, sortOrder]);

  // Reset visible count whenever the filtered set changes (search/sort)
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [filteredAndSortedData]);

  const currentData = filteredAndSortedData.slice(0, visibleCount);
  const hasMore = visibleCount < filteredAndSortedData.length;

  const handleScroll = useCallback(() => {
    const container = tableContainerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollTop + clientHeight >= scrollHeight * 0.5 && visibleCount < filteredAndSortedData.length) {
      setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, filteredAndSortedData.length));
    }
  }, [visibleCount, filteredAndSortedData.length]);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const columns = [
    { key: "name", label: "Column Name" },
    { key: "type", label: "Type" },
    { key: "is_cde", label: "CDE" },
    { key: "metadata", label: "Technical Metadata" },
    { key: "businessterm", label: "Business Description" },
    { key: "transformationLogic", label: "Transformation Logic" },
    { key: "sample", label: "Sample Value" },
  ];

  const renderSortIcon = (column) => {
    if (sortColumn === column) {
      return sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
    }
    return <ArrowUp size={12} className="opacity-30" />;
  };

  const isColumnMatch = (column) => {
    if (!searchQuery || !searchQuery.trim()) return false;
    const searchLower = searchQuery.toLowerCase();
    const searchWithUnderscores = searchLower.replace(/\s+/g, "_");
    const columnName = (column.name || "").toLowerCase();
    const columnType = (column.type || "").toLowerCase();
    const columnMetadata = (column.metadata || "").toLowerCase();
    const columnSample = (column.sample || "").toLowerCase();
    return (
      columnName.includes(searchLower) ||
      columnName.includes(searchWithUnderscores) ||
      columnType.includes(searchLower) ||
      columnType.includes(searchWithUnderscores) ||
      columnMetadata.includes(searchLower) ||
      columnMetadata.includes(searchWithUnderscores) ||
      columnSample.includes(searchLower) ||
      columnSample.includes(searchWithUnderscores)
    );
  };

  const countriesString = assetsOverview?.country || "";
  const countries = countriesString.split(",").map((c) => c.trim()).filter(Boolean);
  const displayed = countries.slice(0, 2);
  const extraCount = countries.length - 2;

  if (!assetData) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">No asset data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 mb-[2rem]">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <Typography
            variant="h5"
            sx={{
              color: "#bb1ccc",
              fontWeight: 700,
              fontSize: "1.5rem",
              marginBottom: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: "4px",
                height: "24px",
                backgroundColor: "#bb1ccc",
                borderRadius: "2px",
              }}
            />
            {assetsOverview?.data_asset_name?.replace(/\|/g, ".") || "Asset Name"}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#000000", fontSize: "14px", fontStyle: "normal", padding: "5px 5px" }}
          >
            {assetsOverview?.data_assets_description || "No description available"}
          </Typography>
        </div>
      </div>

      {/* Table Details Section */}
      <div>
        <div className="flex justify-between items-center mb-2">
          <Typography variant="h6" sx={{ color: "#bb1ccc", fontWeight: 600, fontSize: "1.25rem", marginBottom: 1 }}>
            Table Details
          </Typography>
          {filteredAndSortedData.length > 0 && (
            <span className="text-sm text-gray-500">
              {Math.min(visibleCount, filteredAndSortedData.length)} of {filteredAndSortedData.length} rows
              {tableDetails && filteredAndSortedData.length !== tableDetails.length && (
                <span className="text-gray-400 ml-1">(filtered from {tableDetails.length})</span>
              )}
            </span>
          )}
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div
            ref={tableContainerRef}
            style={{ overflowX: "auto", overflowY: "auto", maxHeight: height ? `${height}px` : "560px" }}
          >
            <table className="w-full">
              <thead
                className="text-[#404040] font-normal"
                style={{ position: "sticky", top: 0, zIndex: 10, backgroundColor: "#F0FDFA" }}
              >
                <tr>
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      className="px-4 pb-[6px] pr-[2px] text-[12px] pt-[10px] text-left whitespace-nowrap cursor-pointer select-none font-normal"
                      style={{ width: "20%" }}
                    >
                      <span className="flex items-center gap-1">
                        {col.label}
                        {renderSortIcon(col.key)}
                      </span>
                    </th>
                  ))}
                </tr>
                <tr>
                  {columns.map((col) => (
                    <th key={`search-${col.key}`} className="px-4 pt-0 pb-[10px] font-normal">
                      <input
                        className="w-full text-[12px] pb-[5px] pl-[10px] placeholder-gray-400 bg-white rounded py-2 outline-none font-normal placeholder:font-normal"
                        placeholder={col.key === "is_cde" ? "Yes/No" : "Search"}
                        value={columnSearch[col.key] || ""}
                        onChange={(e) => handleColumnSearchChange(col.key, e.target.value)}
                      />
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200 bg-white">
                {currentData.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="py-8 text-center text-gray-500">
                      No data found matching your search criteria
                    </td>
                  </tr>
                ) : (
                  currentData.map((column, index) => (
                    <tr
                      key={column.attribute_id || index}
                      className="hover:bg-gray-50 transition-colors"
                      style={{
                        backgroundColor:
                          isColumnMatch(column) && searchQuery
                            ? "#fff3cd"
                            : index % 2 === 0
                            ? "#fafafa"
                            : "#ffffff",
                        border:
                          isColumnMatch(column) && searchQuery ? "2px solid #ffc107" : "none",
                      }}
                    >
                      <td className="py-2 px-4 text-[12px]">
                        <div className="font text-gray-900">{column.name}</div>
                      </td>
                      <td className="py-2 px-4 text-[12px] text-gray-600 capitalize">{column.type}</td>
                      <td className="py-2 px-4 text-[12px] text-gray-600">
                        {column.is_cde ? "Yes" : "No"}
                      </td>
                      <td className="py-2 px-4 text-[12px] text-gray-600" style={{ maxWidth: "300px", wordWrap: "break-word" }}>
                        {renderWithTooltip(column.metadata)}
                      </td>
                      <td className="py-2 px-4 text-[12px] text-gray-600" style={{ maxWidth: "300px", width: "300px", overflow: "hidden" }}>
                        {(() => {
                          const val = column.businessterm || "";
                          const key = `${index}-businessterm`;
                          const isExpanded = expandedCells.has(key);
                          return (
                            <div className="flex items-start gap-1 min-w-0">
                              <span className={`break-words min-w-0 ${isExpanded ? "" : "line-clamp-2"}`}>{val || "N/A"}</span>
                              {val.length > 40 && (
                                <button onClick={() => toggleCell(index, "businessterm")} className="shrink-0 text-gray-500 hover:text-gray-800 mt-0.5" title={isExpanded ? "Collapse" : "Expand"}>
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    {isExpanded ? <path d="M18 15l-6-6-6 6" /> : <path d="M6 9l6 6 6-6" />}
                                  </svg>
                                </button>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="py-2 px-4 text-[12px] text-gray-600" style={{ maxWidth: "200px", width: "200px", overflow: "hidden" }}>
                        {(() => {
                          const val = column.transformationLogic || "";
                          const key = `${index}-transformationLogic`;
                          const isExpanded = expandedCells.has(key);
                          return (
                            <div className="flex items-start gap-1 min-w-0">
                              <span className={`break-words min-w-0 ${isExpanded ? "" : "line-clamp-2"}`}>{val || "N/A"}</span>
                              {val.length > 40 && (
                                <button onClick={() => toggleCell(index, "transformationLogic")} className="shrink-0 text-gray-500 hover:text-gray-800 mt-0.5" title={isExpanded ? "Collapse" : "Expand"}>
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    {isExpanded ? <path d="M18 15l-6-6-6 6" /> : <path d="M6 9l6 6 6-6" />}
                                  </svg>
                                </button>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="py-2 px-4 text-[12px] text-gray-600" style={{ maxWidth: "200px", wordWrap: "break-word" }}>
                        {renderWithTooltip(column.sample)}
                      </td>
                    </tr>
                  ))
                )}

                {/* Sentinel inside the scrollable container */}
                <tr>
                  <td colSpan={columns.length} className="p-0">
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Footer: loading / all loaded */}
          {hasMore && (
            <div className="flex items-center justify-center py-4 gap-2 border-t border-gray-100">
              <img src={heartLoading} alt="Loading more..." style={{ width : 200}} />
              <span className="text-gray-500 text-sm">Loading more...</span>
            </div>
          )}
          {!hasMore && filteredAndSortedData.length > 0 && (
            <div className="text-center py-3 text-gray-400 text-xs border-t border-gray-100">
              All {filteredAndSortedData.length} rows loaded
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssetDetails;
